import React, { useRef, useState } from 'react';
import { PendingAttachments } from './AttachmentList';
import { API_URL, FILE_ACCEPT, usePendingUploads } from './uploads';
import './Submissions.css';

const isHttpUrl = (value) => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (_) {
    return false;
  }
};

// Creator form for submitting campaign content on an accepted bid. When
// `previous` is given (a submission the brand asked changes on), its text
// and links are prefilled so the creator only fixes what was asked.
function SubmitContentModal({ bid, previous, onClose, onSubmitted }) {
  const [title, setTitle] = useState(previous?.title || '');
  const [caption, setCaption] = useState(previous?.caption || '');
  const [notes, setNotes] = useState('');
  const [links, setLinks] = useState(previous?.links?.length ? [...previous.links] : ['']);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const uploads = usePendingUploads();
  const fileInputRef = useRef(null);

  const setLink = (i, value) => setLinks((prev) => prev.map((l, idx) => (idx === i ? value : l)));
  const removeLink = (i) => setLinks((prev) => (prev.length === 1 ? [''] : prev.filter((_, idx) => idx !== i)));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const cleanLinks = links.map((l) => l.trim()).filter(Boolean);
    const badLink = cleanLinks.find((l) => !isHttpUrl(l));
    if (badLink) {
      setError(`"${badLink}" isn't a valid link. Links must start with http:// or https://`);
      return;
    }
    if (uploads.isUploading) {
      setError('Wait for your files to finish uploading.');
      return;
    }
    if (uploads.hasErrors) {
      setError('Some files failed to upload. Retry or remove them first.');
      return;
    }
    if (!uploads.refs.length && !cleanLinks.length) {
      setError('Attach at least one file or link.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          bidId: bid.id,
          title,
          caption,
          notes,
          files: uploads.refs,
          links: cleanLinks,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Failed to submit content');
      if (onSubmitted) onSubmitted(data.submission);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // No close-on-backdrop here: a stray click shouldn't throw away a caption
    // or in-progress uploads.
    <div className="sub-modal-overlay">
      <div className="sub-modal">
        <button type="button" className="sub-modal-close" onClick={onClose}>×</button>
        <h2>{previous ? 'Submit a revised version' : 'Submit content'}</h2>
        <p className="sub-modal-sub">
          for "{bid.jobTitle}"{bid.brandName ? ` · ${bid.brandName}` : ''}. The brand will review it and
          approve or request changes.
        </p>

        {previous?.feedback && (
          <div className="sub-feedback">
            <span className="sub-label">Brand feedback</span>
            <p>{previous.feedback}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="sub-form">
          {error && <div className="sub-error">{error}</div>}

          <div className="sub-field">
            <label htmlFor="sub-title">Title <span className="sub-optional">(optional)</span></label>
            <input
              id="sub-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={255}
              placeholder="e.g. Reel #1 – morning coffee routine"
            />
          </div>

          <div className="sub-field">
            <label htmlFor="sub-caption">Post caption</label>
            <textarea
              id="sub-caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={5}
              placeholder="The caption you'll publish with the post, including hashtags and mentions"
            />
          </div>

          <div className="sub-field">
            <label>Files</label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={FILE_ACCEPT}
              className="sub-file-input"
              onChange={(e) => {
                uploads.addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              className="sub-add-btn"
              onClick={() => fileInputRef.current?.click()}
            >
              + Add photos, videos or documents
            </button>
            <PendingAttachments uploads={uploads} />
          </div>

          <div className="sub-field">
            <label>Links <span className="sub-optional">(Google Docs, Sheets, Drive, Dropbox…)</span></label>
            {links.map((link, i) => (
              <div key={i} className="sub-link-row">
                <input
                  type="text"
                  inputMode="url"
                  value={link}
                  onChange={(e) => setLink(i, e.target.value)}
                  placeholder="https://docs.google.com/…"
                />
                {(links.length > 1 || link) && (
                  <button type="button" className="sub-link-remove" onClick={() => removeLink(i)} aria-label="Remove link">
                    ×
                  </button>
                )}
              </div>
            ))}
            {links.length < 10 && (
              <button type="button" className="sub-add-btn" onClick={() => setLinks((prev) => [...prev, ''])}>
                + Add another link
              </button>
            )}
          </div>

          <div className="sub-field">
            <label htmlFor="sub-notes">Notes for the brand <span className="sub-optional">(optional)</span></label>
            <textarea
              id="sub-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Anything the brand should know (posting date, what changed, etc.)"
            />
          </div>

          <div className="sub-modal-actions">
            <button type="button" className="sub-btn secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="sub-btn primary" disabled={isSubmitting || uploads.isUploading}>
              {isSubmitting ? 'Submitting...' : uploads.isUploading ? 'Uploading...' : 'Submit for review'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SubmitContentModal;
